<template>
  <div class="payments-card members-payments">
    <div class="card-header-row">
      <div class="card-title-left">
        <SectionIcon name="membersPayments" size="40" />
        <h3>Members' payments</h3>
      </div>
      <button class="link-button" :disabled="refreshing" @click="refreshStatuses">
        <i class="bi bi-arrow-clockwise"></i>
        {{ refreshing ? "Refreshing…" : "Refresh from Stripe" }}
      </button>
    </div>

    <!-- Dashboard tiles ------------------------------------------------- -->
    <div class="stat-tiles">
      <button
        class="stat-tile"
        :class="{ selected: statusFilter === 'active' }"
        @click="toggleStatusFilter('active')"
      >
        <span class="stat-value">{{ stats.onPlan }}</span>
        <span class="stat-label">On a plan</span>
      </button>
      <button
        class="stat-tile"
        :class="{ selected: statusFilter === 'paid' }"
        @click="toggleStatusFilter('paid')"
      >
        <span class="stat-value">{{ stats.oneOffPaid }}</span>
        <span class="stat-label">Paid one-off</span>
      </button>
      <button
        class="stat-tile"
        :class="{ selected: statusFilter === 'action', attention: stats.needsAction > 0 }"
        @click="toggleStatusFilter('action')"
      >
        <span class="stat-value">{{ stats.needsAction }}</span>
        <span class="stat-label">
          <i v-if="stats.needsAction > 0" class="bi bi-exclamation-circle"></i>
          Needs action
        </span>
      </button>
      <div class="stat-tile stat-tile--static">
        <span class="stat-value">{{ pounds(stats.monthlyPence) }}</span>
        <span class="stat-label">Est. monthly income</span>
      </div>
    </div>

    <!-- Search + filters -------------------------------------------------- -->
    <div class="members-filters">
      <input
        type="search"
        v-model="search"
        placeholder="Search by player or email…"
        class="members-search"
      />
      <div class="filter-chips">
        <button
          v-for="chip in filterChips"
          :key="chip.key"
          class="chip"
          :class="{ selected: statusFilter === chip.key }"
          @click="toggleStatusFilter(chip.key)"
        >
          {{ chip.label }}
        </button>
      </div>
      <select v-model="planFilter" class="plan-filter" v-if="planFilterOptions.length > 0">
        <option value="all">All plans</option>
        <option v-for="plan in planFilterOptions" :key="plan.id" :value="String(plan.id)">
          {{ plan.name }}
        </option>
        <option value="none">No plan</option>
      </select>
    </div>

    <div class="link-plan-row" v-if="activePlans.length > 0">
      <label for="linkPlanSelect">Plan used for payment links:</label>
      <select id="linkPlanSelect" v-model="selectedLinkPlanId">
        <option v-for="plan in activePlans" :key="plan.id" :value="plan.id">
          {{ plan.name }} — {{ pounds(plan.amount_pence) }}
          {{ intervalLabel(plan.billing_interval).toLowerCase() }}
        </option>
      </select>
    </div>

    <!-- Members table ----------------------------------------------------- -->
    <p class="muted" v-if="loading">Loading members…</p>
    <template v-else>
      <div class="result-row">
        <p class="muted result-count">
          {{ filteredMembers.length }} of {{ members.length }} members
        </p>
        <button
          v-if="bulkEmailable.length > 0 && activePlans.length > 0"
          class="action-chip"
          :disabled="actionBusy"
          @click="emailBulkPaymentLinks"
        >
          <i class="bi bi-envelope"></i>
          Email payment links to {{ bulkEmailable.length }} members shown
        </button>
      </div>

      <table class="payments-table" v-if="pagedMembers.length > 0">
        <thead>
          <tr>
            <th></th>
            <th>Member</th>
            <th>Age group</th>
            <th>Plan</th>
            <th>Status</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <template v-for="member in pagedMembers" :key="member.user_id">
            <tr
              class="member-row"
              :class="{ expanded: expandedUserId === member.user_id }"
              @click="toggleExpand(member)"
            >
              <td class="expand-cell">
                <i class="bi" :class="expandedUserId === member.user_id ? 'bi-chevron-down' : 'bi-chevron-right'"></i>
              </td>
              <td>
                {{ member.name }}
                <span class="member-email">{{ member.email }}</span>
              </td>
              <td>{{ member.age_group }}</td>
              <td>
                <span v-if="member.plan_name">
                  {{ member.plan_name }} ({{ pounds(member.amount_pence) }}
                  {{ member.plan_type === "installments" ? "in installments" : intervalLabel(member.billing_interval).toLowerCase() }})
                </span>
                <span v-else class="muted">—</span>
              </td>
              <td>
                <template v-if="member.status === 'installments'">
                  <span
                    class="status-pill"
                    :class="member.cleared ? 'status-good' : (member.next_due && member.next_due.status === 'overdue' ? 'status-bad' : 'status-warn')"
                  >
                    {{ member.cleared ? "Cleared" : (member.next_due ? member.next_due.label + " due" : "Installments") }}
                  </span>
                  <span
                    v-if="!member.cleared && member.next_due && member.next_due.due_date"
                    class="ends-note"
                  >
                    {{ pounds(member.next_due.amount_pence) }} by {{ formatDate(member.next_due.due_date) }}
                  </span>
                </template>
                <template v-else>
                  <span class="status-pill" :class="statusClass(member.status)">
                    {{ statusLabel(member.status) }}
                  </span>
                  <span
                    v-if="member.cancel_at_period_end == 1 && member.status === 'active'"
                    class="ends-note"
                  >
                    ends {{ formatDate(member.current_period_end) }}
                  </span>
                </template>
              </td>
              <td @click.stop class="row-actions">
                <template v-if="needsPaymentLink(member) && activePlans.length > 0">
                  <button
                    class="link-button"
                    :disabled="linkBusyUserId === member.user_id"
                    @click="createPaymentLink(member)"
                  >
                    {{ linkBusyUserId === member.user_id ? "Working…" : "Copy link" }}
                  </button>
                  <button
                    class="link-button"
                    :disabled="linkBusyUserId === member.user_id"
                    @click="emailPaymentLink(member)"
                  >
                    Email link
                  </button>
                </template>
              </td>
            </tr>
            <tr v-if="expandedUserId === member.user_id" class="history-row">
              <td colspan="6">
                <div class="history-panel">
                  <h4>Payment history — {{ member.name }}</h4>
                  <p class="muted" v-if="historyLoading">Loading history…</p>
                  <table v-else-if="history.length > 0" class="history-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Plan / reason</th>
                        <th>Amount</th>
                        <th>Billing</th>
                        <th>Method</th>
                        <th>Status</th>
                        <th>Paid up to</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="record in history" :key="record.id">
                        <td>{{ formatDate(record.created_at) }}</td>
                        <td>
                          {{ record.plan_name || (record.payment_method === "waived" ? "Fee waiver" : "Manual payment") }}
                          <span class="record-note" v-if="record.note">{{ record.note }}</span>
                        </td>
                        <td>{{ record.amount_pence ? pounds(record.amount_pence) : "—" }}</td>
                        <td>{{ intervalLabel(record.billing_interval) }}</td>
                        <td>{{ methodLabel(record.payment_method) }}</td>
                        <td>
                          <span class="status-pill" :class="statusClass(record.status)">
                            {{ statusLabel(record.status) }}
                          </span>
                          <span
                            v-if="record.cancel_at_period_end == 1 && record.status === 'active'"
                            class="ends-note"
                          >
                            ends {{ formatDate(record.current_period_end) }}
                          </span>
                        </td>
                        <td>{{ record.current_period_end ? formatDate(record.current_period_end) : "—" }}</td>
                        <td>
                          <button
                            v-if="record.payment_method !== 'stripe'"
                            class="link-button danger"
                            title="Remove this record"
                            :disabled="actionBusy"
                            @click="deleteRecord(member, record)"
                          >
                            <i class="bi bi-trash"></i>
                          </button>
                          <button
                            v-else-if="record.status === 'paid' && !record.stripe_subscription_id"
                            class="link-button danger"
                            :disabled="actionBusy"
                            @click="refundRecord(member, record)"
                          >
                            Refund
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  <p class="muted" v-else>
                    No payment records — this member has never started a checkout.
                  </p>

                  <!-- Season fees (installments) -------------------------- -->
                  <div
                    v-if="member.plan_type === 'installments' && member.installment_ledger"
                    class="installment-panel"
                  >
                    <h4>Season fees — {{ member.plan_name }}</h4>
                    <table class="history-table">
                      <thead>
                        <tr>
                          <th>Stage</th>
                          <th>Amount</th>
                          <th>Voucher</th>
                          <th>Paid</th>
                          <th>Outstanding</th>
                          <th>Due</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="inst in member.installment_ledger.installments" :key="inst.id">
                          <td>{{ inst.label }}</td>
                          <td>{{ pounds(inst.amount_pence) }}</td>
                          <td>{{ inst.discount_pence ? "−" + pounds(inst.discount_pence) : "—" }}</td>
                          <td>{{ inst.paid_pence ? pounds(inst.paid_pence) : "—" }}</td>
                          <td>
                            <span class="status-pill" :class="inst.status === 'paid' ? 'status-good' : (inst.status === 'overdue' ? 'status-bad' : 'status-warn')">
                              {{ inst.status === "paid" ? "Settled" : pounds(inst.outstanding_pence) }}
                            </span>
                          </td>
                          <td>{{ inst.due_date ? formatDate(inst.due_date) : "—" }}</td>
                          <td class="row-actions">
                            <template v-if="inst.outstanding_pence > 0">
                              <button
                                class="link-button"
                                :disabled="actionBusy"
                                @click="copyInstallmentLink(member, inst)"
                              >
                                Copy pay link
                              </button>
                              <button
                                class="link-button"
                                :disabled="actionBusy"
                                @click="openInstallmentCash(inst)"
                              >
                                Record cash
                              </button>
                            </template>
                          </td>
                        </tr>
                      </tbody>
                    </table>

                    <div class="inline-form" v-if="instCashFor">
                      <label>
                        Amount ({{ currencySymbol }}) — {{ instCashFor.label }}
                        <input type="number" min="0.01" step="0.01" v-model="instCashForm.amountPounds" />
                      </label>
                      <label>
                        Method
                        <select v-model="instCashForm.method">
                          <option value="cash">Cash</option>
                          <option value="bank_transfer">Bank transfer</option>
                          <option value="other">Other</option>
                        </select>
                      </label>
                      <label class="grow">
                        Note (optional)
                        <input type="text" v-model="instCashForm.note" />
                      </label>
                      <button class="action-chip confirm" :disabled="actionBusy" @click="saveInstallmentCash(member)">
                        {{ actionBusy ? "Saving…" : "Save payment" }}
                      </button>
                    </div>

                    <div v-if="voucherScheme" class="voucher-claim">
                      <button
                        class="action-chip"
                        :disabled="actionBusy"
                        @click="showVoucherForm = !showVoucherForm"
                      >
                        {{ showVoucherForm ? "Close" : "Add " + voucherScheme.label + " claim" }}
                      </button>
                      <div class="inline-form" v-if="showVoucherForm">
                        <label class="grow">
                          School
                          <input type="text" v-model="voucherForm.school" />
                        </label>
                        <label>
                          Year level
                          <input type="text" v-model="voucherForm.year_level" placeholder="e.g. Year 7" />
                        </label>
                        <button class="action-chip confirm" :disabled="actionBusy" @click="saveVoucherClaim(member)">
                          {{ actionBusy ? "Saving…" : "Claim " + pounds(voucherScheme.amount_pence) }}
                        </button>
                      </div>
                    </div>
                  </div>

                  <!-- Start season fees for members not yet on installments -->
                  <div
                    v-else-if="installmentPlans.length > 0 && member.plan_type !== 'installments'"
                    class="installment-panel"
                  >
                    <div class="inline-form">
                      <label>
                        Season fees plan
                        <select v-model="startPlanId">
                          <option v-for="plan in installmentPlans" :key="plan.id" :value="plan.id">
                            {{ plan.name }} ({{ pounds(plan.amount_pence) }})
                          </option>
                        </select>
                      </label>
                      <button class="action-chip" :disabled="actionBusy || !startPlanId" @click="startSeasonFees(member)">
                        Set up season fees
                      </button>
                    </div>
                  </div>

                  <!-- Treasurer actions ---------------------------------- -->
                  <div class="member-actions">
                    <template v-if="cancellableRecord">
                      <button
                        v-if="cancellableRecord.cancel_at_period_end != 1"
                        class="action-chip"
                        :disabled="actionBusy"
                        @click="cancelSubscription(member, false)"
                      >
                        Cancel at period end
                      </button>
                      <button
                        class="action-chip danger"
                        :disabled="actionBusy"
                        @click="cancelSubscription(member, true)"
                      >
                        Cancel immediately
                      </button>
                    </template>
                    <button
                      class="action-chip"
                      :disabled="actionBusy"
                      @click="showManualForm = !showManualForm; showWaiveForm = false"
                    >
                      Record manual payment
                    </button>
                    <button
                      class="action-chip"
                      :disabled="actionBusy"
                      @click="showWaiveForm = !showWaiveForm; showManualForm = false"
                    >
                      Waive fees
                    </button>
                    <button
                      v-if="hasStripeHistory"
                      class="action-chip"
                      :disabled="actionBusy"
                      @click="copyPortalLink(member)"
                      title="Parent can update their card, see invoices and cancel"
                    >
                      Copy card-update link
                    </button>
                  </div>

                  <div class="inline-form" v-if="showManualForm">
                    <label>
                      Amount ({{ currencySymbol }})
                      <input type="number" min="0.01" step="0.01" v-model="manualForm.amountPounds" />
                    </label>
                    <label>
                      Method
                      <select v-model="manualForm.method">
                        <option value="cash">Cash</option>
                        <option value="bank_transfer">Bank transfer</option>
                        <option value="other">Other</option>
                      </select>
                    </label>
                    <label class="grow">
                      Note (optional)
                      <input type="text" v-model="manualForm.note" placeholder="e.g. paid at training 12 Jul" />
                    </label>
                    <button class="action-chip confirm" :disabled="actionBusy" @click="saveManualPayment(member)">
                      {{ actionBusy ? "Saving…" : "Save payment" }}
                    </button>
                  </div>

                  <div class="inline-form" v-if="showWaiveForm">
                    <label class="grow">
                      Reason (kept private to club admins)
                      <input type="text" v-model="waiveNote" placeholder="e.g. hardship agreed with committee" />
                    </label>
                    <button class="action-chip confirm" :disabled="actionBusy" @click="saveWaiver(member)">
                      {{ actionBusy ? "Saving…" : "Waive fees" }}
                    </button>
                  </div>

                  <p class="error-text" v-if="actionError">{{ actionError }}</p>
                </div>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
      <p v-else class="muted">No members match.</p>

      <div class="pagination" v-if="pageCount > 1">
        <button class="link-button" :disabled="page === 1" @click="page--">
          &laquo; Previous
        </button>
        <span class="muted">Page {{ page }} of {{ pageCount }}</span>
        <button class="link-button" :disabled="page === pageCount" @click="page++">
          Next &raquo;
        </button>
      </div>
    </template>

    <p v-if="linkMessage" class="muted">{{ linkMessage }}</p>
  </div>
</template>

<script>
import {
  getPaymentsCurrency,
  getCurrencySymbol,
  getVoucherConfig,
} from "@/config/tenant";
import { StripeConnect } from "@/services/stripe-connect-service.js";
import SectionIcon from "@/components/SectionIcon/index.vue";

const PAGE_SIZE = 25;
// Statuses that count as "sorted, nothing to chase"
const SETTLED_STATUSES = ["active", "paid", "trialing", "waived"];

export default {
  name: "MembersPayments",
  components: { SectionIcon },
  props: {
    clubId: {
      type: String,
      required: true,
    },
    activePlans: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      members: [],
      loading: true,
      refreshing: false,
      search: "",
      statusFilter: "all",
      planFilter: "all",
      page: 1,
      expandedUserId: null,
      history: [],
      historyLoading: false,
      selectedLinkPlanId: null,
      linkBusyUserId: null,
      linkMessage: "",
      filterChips: [
        { key: "all", label: "All" },
        { key: "action", label: "Needs action" },
        { key: "active", label: "On a plan" },
        { key: "paid", label: "Paid one-off" },
        { key: "waived", label: "Waived" },
        { key: "none", label: "No plan" },
      ],
      actionBusy: false,
      actionError: "",
      showManualForm: false,
      manualForm: { amountPounds: null, method: "cash", note: "" },
      showWaiveForm: false,
      waiveNote: "",
      instCashFor: null,
      instCashForm: { amountPounds: null, method: "cash", note: "" },
      showVoucherForm: false,
      voucherForm: { school: "", year_level: "" },
      startPlanId: null,
    };
  },
  computed: {
    currencySymbol() {
      return getCurrencySymbol();
    },
    voucherScheme() {
      return getVoucherConfig();
    },
    installmentPlans() {
      return this.activePlans.filter((plan) => plan.plan_type === "installments");
    },
    stats() {
      let onPlan = 0;
      let oneOffPaid = 0;
      let needsAction = 0;
      let monthlyPence = 0;
      for (const member of this.members) {
        if (member.status === "active" || member.status === "trialing") {
          onPlan++;
          if (member.status === "active") {
            if (member.billing_interval === "month") {
              monthlyPence += Number(member.amount_pence) || 0;
            } else if (member.billing_interval === "year") {
              monthlyPence += Math.round((Number(member.amount_pence) || 0) / 12);
            }
          }
        } else if (member.status === "paid") {
          oneOffPaid++;
        } else if (member.status === "waived") {
          // settled by decision - appears in no tile
        } else if (member.status === "installments") {
          if (member.cleared) oneOffPaid++;
          else needsAction++;
        } else {
          // pending / past_due / canceled / incomplete / no plan at all
          needsAction++;
        }
      }
      return { onPlan, oneOffPaid, needsAction, monthlyPence };
    },
    planFilterOptions() {
      // Plans present in the data plus currently active ones, deduped
      const byId = new Map();
      for (const plan of this.activePlans) {
        byId.set(String(plan.id), { id: plan.id, name: plan.name });
      }
      for (const member of this.members) {
        if (member.plan_id && !byId.has(String(member.plan_id))) {
          byId.set(String(member.plan_id), {
            id: member.plan_id,
            name: member.plan_name,
          });
        }
      }
      return Array.from(byId.values());
    },
    filteredMembers() {
      const query = this.search.trim().toLowerCase();
      return this.members.filter((member) => {
        if (query) {
          const haystack = `${member.name || ""} ${member.email || ""}`.toLowerCase();
          if (!haystack.includes(query)) return false;
        }
        if (this.planFilter === "none") {
          if (member.plan_id) return false;
        } else if (this.planFilter !== "all") {
          if (String(member.plan_id) !== this.planFilter) return false;
        }
        switch (this.statusFilter) {
          case "active":
            return member.status === "active" || member.status === "trialing";
          case "paid":
            return member.status === "paid";
          case "waived":
            return member.status === "waived";
          case "none":
            return !member.subscription_id;
          case "action":
            return !this.isSettled(member);
          default:
            return true;
        }
      });
    },
    pageCount() {
      return Math.max(1, Math.ceil(this.filteredMembers.length / PAGE_SIZE));
    },
    // Members in the current filtered view who could be chased for payment
    bulkEmailable() {
      return this.filteredMembers.filter(
        (member) => !this.isSettled(member) && member.email,
      );
    },
    // Whether the expanded member has any Stripe-backed record (portal-able)
    hasStripeHistory() {
      return this.history.some((record) => record.payment_method === "stripe");
    },
    // The expanded member's live subscription record, if any (cancellable)
    cancellableRecord() {
      return (
        this.history.find(
          (record) =>
            record.stripe_subscription_id &&
            ["active", "trialing", "past_due"].includes(record.status),
        ) || null
      );
    },
    pagedMembers() {
      const start = (this.page - 1) * PAGE_SIZE;
      return this.filteredMembers.slice(start, start + PAGE_SIZE);
    },
  },
  watch: {
    search() {
      this.page = 1;
    },
    statusFilter() {
      this.page = 1;
    },
    planFilter() {
      this.page = 1;
    },
    activePlans: {
      immediate: true,
      handler(plans) {
        if (
          plans.length > 0 &&
          !plans.some((plan) => plan.id === this.selectedLinkPlanId)
        ) {
          this.selectedLinkPlanId = plans[0].id;
        }
      },
    },
  },
  created() {
    this.loadMembers();
  },
  methods: {
    pounds(amountPence) {
      return new Intl.NumberFormat("en-GB", {
        style: "currency",
        currency: getPaymentsCurrency(),
      }).format((amountPence || 0) / 100);
    },
    intervalLabel(interval) {
      if (interval === "month") return "Monthly";
      if (interval === "year") return "Yearly";
      if (interval === "one_off") return "One-off";
      return "—";
    },
    formatDate(value) {
      if (!value) return "—";
      const date = new Date(String(value).replace(" ", "T"));
      if (isNaN(date)) return value;
      return date.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    },
    statusLabel(status) {
      if (!status) return "No plan";
      const labels = {
        pending: "Checkout not completed",
        active: "Active",
        paid: "Paid",
        trialing: "Trial",
        waived: "Fees waived",
        refunded: "Refunded",
        past_due: "Payment failed",
        canceled: "Cancelled",
        incomplete: "Payment incomplete",
        incomplete_expired: "Checkout expired",
        unpaid: "Unpaid",
      };
      return labels[status] || status;
    },
    statusClass(status) {
      if (status === "waived" || status === "refunded") return "status-neutral";
      if (SETTLED_STATUSES.includes(status)) return "status-good";
      if (["past_due", "canceled", "unpaid", "incomplete_expired"].includes(status)) {
        return "status-bad";
      }
      return "status-warn";
    },
    methodLabel(method) {
      const labels = {
        stripe: "Card",
        cash: "Cash",
        bank_transfer: "Bank transfer",
        other: "Other",
        waived: "—",
      };
      return labels[method] || method || "Card";
    },
    needsPaymentLink(member) {
      // Installment members get per-stage links in the drill-down instead
      if (member.status === "installments") return false;
      return !SETTLED_STATUSES.includes(member.status);
    },
    isSettled(member) {
      if (member.status === "installments") return !!member.cleared;
      return SETTLED_STATUSES.includes(member.status);
    },
    ensureStartPlanDefault() {
      if (!this.startPlanId && this.installmentPlans.length > 0) {
        this.startPlanId = this.installmentPlans[0].id;
      }
    },
    toggleStatusFilter(key) {
      this.statusFilter = this.statusFilter === key ? "all" : key;
    },
    resetActionForms() {
      this.actionError = "";
      this.showManualForm = false;
      this.manualForm = { amountPounds: null, method: "cash", note: "" };
      this.showWaiveForm = false;
      this.waiveNote = "";
    },
    async toggleExpand(member) {
      this.resetActionForms();
      this.instCashFor = null;
      this.showVoucherForm = false;
      this.ensureStartPlanDefault();
      if (this.expandedUserId === member.user_id) {
        this.expandedUserId = null;
        return;
      }
      this.expandedUserId = member.user_id;
      this.history = [];
      this.historyLoading = true;
      try {
        const response = await StripeConnect.getMemberPaymentHistory(
          this.clubId,
          member.user_id,
        );
        this.history = response.data.data || [];
      } catch (error) {
        this.history = [];
      } finally {
        this.historyLoading = false;
      }
    },
    async refreshExpanded(member) {
      // Reload both the list and the open drill-down after an action
      await this.loadMembers();
      try {
        const response = await StripeConnect.getMemberPaymentHistory(
          this.clubId,
          member.user_id,
        );
        this.history = response.data.data || [];
      } catch (error) {
        this.history = [];
      }
    },
    async cancelSubscription(member, immediately) {
      const question = immediately
        ? `Cancel ${member.name}'s subscription immediately? Billing stops now.`
        : `Cancel ${member.name}'s subscription at the end of the paid period?`;
      if (!window.confirm(question)) return;
      this.actionBusy = true;
      this.actionError = "";
      try {
        await StripeConnect.cancelSubscription(
          this.clubId,
          this.cancellableRecord.id,
          immediately,
        );
        await this.refreshExpanded(member);
      } catch (error) {
        this.actionError =
          error?.response?.data?.error || "Could not cancel the subscription.";
      } finally {
        this.actionBusy = false;
      }
    },
    async saveManualPayment(member) {
      const amountPence = Math.round(
        parseFloat(this.manualForm.amountPounds || 0) * 100,
      );
      if (!(amountPence > 0)) {
        this.actionError = "Enter an amount.";
        return;
      }
      this.actionBusy = true;
      this.actionError = "";
      try {
        await StripeConnect.recordManualPayment({
          clubId: this.clubId,
          userId: member.user_id,
          amount_pence: amountPence,
          method: this.manualForm.method,
          note: this.manualForm.note,
        });
        this.resetActionForms();
        await this.refreshExpanded(member);
      } catch (error) {
        this.actionError =
          error?.response?.data?.error || "Could not save the payment.";
      } finally {
        this.actionBusy = false;
      }
    },
    async saveWaiver(member) {
      this.actionBusy = true;
      this.actionError = "";
      try {
        await StripeConnect.waiveFees(this.clubId, member.user_id, this.waiveNote);
        this.resetActionForms();
        await this.refreshExpanded(member);
      } catch (error) {
        this.actionError =
          error?.response?.data?.error || "Could not waive the fees.";
      } finally {
        this.actionBusy = false;
      }
    },
    async deleteRecord(member, record) {
      const what = record.payment_method === "waived" ? "fee waiver" : "manual payment record";
      if (!window.confirm(`Remove this ${what}?`)) return;
      this.actionBusy = true;
      this.actionError = "";
      try {
        await StripeConnect.deleteFeeRecord(this.clubId, record.id);
        await this.refreshExpanded(member);
      } catch (error) {
        this.actionError =
          error?.response?.data?.error || "Could not remove the record.";
      } finally {
        this.actionBusy = false;
      }
    },
    openInstallmentCash(inst) {
      this.instCashFor = this.instCashFor?.id === inst.id ? null : inst;
      this.instCashForm = {
        amountPounds: (inst.outstanding_pence / 100).toFixed(2),
        method: "cash",
        note: "",
      };
    },
    async saveInstallmentCash(member) {
      this.actionBusy = true;
      this.actionError = "";
      try {
        await StripeConnect.recordInstallmentPayment({
          clubId: this.clubId,
          userId: member.user_id,
          feePlanId: member.plan_id,
          installmentId: this.instCashFor.id,
          amount_pence: Math.round(parseFloat(this.instCashForm.amountPounds || 0) * 100),
          method: this.instCashForm.method,
          note: this.instCashForm.note,
        });
        this.instCashFor = null;
        await this.loadMembers();
      } catch (error) {
        this.actionError = error?.response?.data?.error || "Could not save the payment.";
      } finally {
        this.actionBusy = false;
      }
    },
    async copyInstallmentLink(member, inst) {
      this.actionBusy = true;
      this.actionError = "";
      try {
        const response = await StripeConnect.createInstallmentCheckout(
          this.clubId,
          member.user_id,
          member.plan_id,
          inst.id,
        );
        await navigator.clipboard.writeText(response.data.data.url);
        this.linkMessage = `Payment link for ${member.name} (${inst.label}) copied — valid for 24 hours.`;
      } catch (error) {
        this.actionError = error?.response?.data?.error || "Could not create the link.";
      } finally {
        this.actionBusy = false;
      }
    },
    async saveVoucherClaim(member) {
      this.actionBusy = true;
      this.actionError = "";
      try {
        await StripeConnect.claimVoucher({
          clubId: this.clubId,
          userId: member.user_id,
          feePlanId: member.plan_id,
          details: {
            child_name: member.name,
            school: this.voucherForm.school,
            year_level: this.voucherForm.year_level,
          },
        });
        this.showVoucherForm = false;
        this.voucherForm = { school: "", year_level: "" };
        await this.loadMembers();
      } catch (error) {
        this.actionError = error?.response?.data?.error || "Could not save the claim.";
      } finally {
        this.actionBusy = false;
      }
    },
    async startSeasonFees(member) {
      this.actionBusy = true;
      this.actionError = "";
      try {
        await StripeConnect.getInstallmentStatus(
          this.clubId,
          member.user_id,
          this.startPlanId,
        );
        await this.loadMembers();
      } catch (error) {
        this.actionError = error?.response?.data?.error || "Could not set up season fees.";
      } finally {
        this.actionBusy = false;
      }
    },
    async loadMembers() {
      this.loading = true;
      try {
        const response = await StripeConnect.getMembersPaymentStatus(this.clubId);
        this.members = response.data.data || [];
      } catch (error) {
        console.log(error);
      } finally {
        this.loading = false;
      }
    },
    async refreshStatuses() {
      this.refreshing = true;
      try {
        await StripeConnect.refreshMemberStatuses(this.clubId);
        await this.loadMembers();
      } catch (error) {
        console.log(error);
      } finally {
        this.refreshing = false;
      }
    },
    selectedLinkPlan() {
      return (
        this.activePlans.find((plan) => plan.id === this.selectedLinkPlanId) ||
        null
      );
    },
    async createPaymentLink(member) {
      this.linkBusyUserId = member.user_id;
      this.linkMessage = "";
      try {
        const activePlan = this.selectedLinkPlan();
        if (!activePlan) {
          this.linkMessage = "Create an active fee plan first.";
          return;
        }
        // Permanent per-member payment link - safe to paste anywhere,
        // never expires (unlike checkout-session URLs)
        const response = await StripeConnect.getMemberPaymentLink(
          this.clubId,
          activePlan.id,
          member.user_id,
        );
        const url = response.data.data.url;
        await navigator.clipboard.writeText(url);
        this.linkMessage = `Payment link for ${member.name} copied to clipboard (${activePlan.name}).`;
      } catch (error) {
        this.linkMessage =
          error?.response?.data?.error || "Could not create the payment link.";
      } finally {
        this.linkBusyUserId = null;
      }
    },
    async emailPaymentLink(member) {
      const activePlan = this.selectedLinkPlan();
      if (!activePlan) {
        this.linkMessage = "Create an active fee plan first.";
        return;
      }
      this.linkBusyUserId = member.user_id;
      this.linkMessage = "";
      try {
        const response = await StripeConnect.emailPaymentLinks(
          this.clubId,
          activePlan.id,
          [member.user_id],
        );
        const result = response.data.data;
        this.linkMessage =
          result.sent > 0
            ? `Payment link emailed to ${member.name} (${activePlan.name}).`
            : `Could not email ${member.name}: ${(result.failed || []).join(", ")}`;
      } catch (error) {
        this.linkMessage =
          error?.response?.data?.error || "Could not send the email.";
      } finally {
        this.linkBusyUserId = null;
      }
    },
    async emailBulkPaymentLinks() {
      const activePlan = this.selectedLinkPlan();
      if (!activePlan) {
        this.linkMessage = "Create an active fee plan first.";
        return;
      }
      const targets = this.bulkEmailable;
      if (
        !window.confirm(
          `Email a ${activePlan.name} payment link to ${targets.length} members?`,
        )
      ) {
        return;
      }
      this.actionBusy = true;
      this.linkMessage = "";
      try {
        const response = await StripeConnect.emailPaymentLinks(
          this.clubId,
          activePlan.id,
          targets.map((member) => member.user_id),
        );
        const result = response.data.data;
        this.linkMessage =
          `Emailed ${result.sent} payment links.` +
          (result.failed?.length
            ? ` Failed: ${result.failed.join(", ")}`
            : "");
      } catch (error) {
        this.linkMessage =
          error?.response?.data?.error || "Could not send the emails.";
      } finally {
        this.actionBusy = false;
      }
    },
    async refundRecord(member, record) {
      if (
        !window.confirm(
          `Refund ${this.pounds(record.amount_pence)} to ${member.name}? This cannot be undone.`,
        )
      ) {
        return;
      }
      this.actionBusy = true;
      this.actionError = "";
      try {
        await StripeConnect.refundPayment(this.clubId, record.id);
        await this.refreshExpanded(member);
      } catch (error) {
        this.actionError =
          error?.response?.data?.error || "Could not refund the payment.";
      } finally {
        this.actionBusy = false;
      }
    },
    async copyPortalLink(member) {
      this.actionBusy = true;
      this.actionError = "";
      try {
        const response = await StripeConnect.createBillingPortalLink(
          this.clubId,
          member.user_id,
        );
        await navigator.clipboard.writeText(response.data.data.url);
        this.actionError = "";
        this.linkMessage = `Card-update link for ${member.name} copied - send it to the parent. (Link is valid for a short time.)`;
      } catch (error) {
        this.actionError =
          error?.response?.data?.error || "Could not create the portal link.";
      } finally {
        this.actionBusy = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.members-payments {
  .card-header-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.5rem;

    h3 {
      margin: 0;
    }

    .card-title-left {
      display: flex;
      align-items: center;
      gap: 0.75rem;
    }
  }

  .muted {
    color: #777;
    font-size: 0.9rem;
  }

  /* Stat tiles: numbers in ink, not status colour; the attention state gets
     an icon + border so it is never colour-alone */
  .stat-tiles {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
    margin: 1rem 0;
  }

  .stat-tile {
    flex: 1 1 150px;
    max-width: 240px;
    background: #f7f8fa;
    border: 1px solid #e3e3e3;
    border-radius: 8px;
    padding: 0.9rem 1rem;
    text-align: left;
    cursor: pointer;
    font: inherit;
    color: #222;
    transition: background 0.15s ease, border-color 0.15s ease;

    &:hover:not(.stat-tile--static) {
      background: #eef4fb;
      border-color: #1e87f0;
    }

    .stat-value {
      display: block;
      font-size: 1.5rem;
      font-weight: 700;
      color: #222;
    }

    .stat-label {
      display: block;
      margin-top: 0.15rem;
      font-size: 0.8rem;
      color: #666;

      i {
        color: #9a6b13;
        margin-right: 0.2rem;
      }
    }

    &.attention {
      border-color: #e8c98a;
      background: #fdf8ee;
    }

    &.selected {
      border-color: #1e87f0;
      box-shadow: 0 0 0 1px #1e87f0 inset;
    }

    &.stat-tile--static {
      cursor: default;
    }
  }

  .members-filters {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 0.5rem;

    .members-search {
      flex: 1 1 220px;
      max-width: 320px;
      padding: 0.4rem 0.6rem;
      border: 1px solid #ccc;
      border-radius: 4px;
    }

    .filter-chips {
      display: flex;
      flex-wrap: wrap;
      gap: 0.35rem;
    }

    .chip {
      border: 1px solid #ccc;
      background: #fff;
      /* Explicit ink: the app's global button styles paint text white,
         which made unselected chips look empty */
      color: #444;
      border-radius: 999px;
      padding: 0.25rem 0.75rem;
      font-size: 0.8rem;
      line-height: 1.4;
      cursor: pointer;
      transition: background 0.15s ease, border-color 0.15s ease;

      &:hover {
        background: #e8f2fd;
        border-color: #1e87f0;
        color: #14508f;
      }

      &.selected {
        background: #1e87f0;
        border-color: #1e87f0;
        color: #fff;

        &:hover {
          background: #1a78d6;
          color: #fff;
        }
      }
    }

    .plan-filter {
      flex: 0 0 auto;
      width: auto;
      max-width: 220px;
      padding: 0.35rem 0.5rem;
      border: 1px solid #ccc;
      border-radius: 4px;
      font-size: 0.85rem;
    }
  }

  .link-plan-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin: 0.5rem 0;
    font-size: 0.9rem;

    select {
      padding: 0.25rem 0.5rem;
      border: 1px solid #ccc;
      border-radius: 4px;
    }
  }

  .result-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .result-count {
    margin: 0.25rem 0;
  }

  .row-actions {
    white-space: nowrap;

    .link-button + .link-button {
      margin-left: 0.75rem;
    }
  }

  .payments-table {
    width: 100%;
    border-collapse: collapse;
    margin: 0.5rem 0;

    th,
    td {
      text-align: left;
      padding: 0.5rem 0.75rem;
      border-bottom: 1px solid #eee;
    }
  }

  .member-row {
    cursor: pointer;

    &:hover {
      background: #f7f9fc;
    }

    &.expanded {
      background: #f2f7fd;
    }

    .expand-cell {
      width: 1.5rem;
      color: #888;
    }

    .member-email {
      display: block;
      font-size: 0.75rem;
      color: #999;
    }
  }

  .history-row td {
    background: #fafbfd;
    padding: 0;
  }

  .history-panel {
    padding: 0.75rem 1rem 1rem 2.25rem;

    h4 {
      margin: 0.25rem 0 0.5rem;
      font-size: 0.95rem;
    }
  }

  .history-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.85rem;

    th,
    td {
      text-align: left;
      padding: 0.35rem 0.6rem;
      border-bottom: 1px solid #eee;
    }

    th {
      color: #777;
      font-weight: 600;
    }
  }

  .status-pill {
    display: inline-block;
    padding: 0.15rem 0.6rem;
    border-radius: 999px;
    font-size: 0.8rem;

    &.status-good {
      background: #e6f6ec;
      color: #1e7b3c;
    }
    &.status-warn {
      background: #fdf3e0;
      color: #9a6b13;
    }
    &.status-bad {
      background: #fdeaea;
      color: #b03030;
    }
    &.status-neutral {
      background: #eef0f3;
      color: #55606b;
    }
  }

  .ends-note {
    display: inline-block;
    margin-left: 0.4rem;
    font-size: 0.75rem;
    color: #9a6b13;
  }

  .record-note {
    display: block;
    font-size: 0.75rem;
    color: #999;
  }

  .member-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-top: 0.75rem;
  }

  .action-chip {
    border: 1px solid #ccc;
    background: #fff;
    color: #444;
    border-radius: 6px;
    padding: 0.3rem 0.75rem;
    font-size: 0.8rem;
    cursor: pointer;
    transition: background 0.15s ease, border-color 0.15s ease;

    &:hover:not(:disabled) {
      background: #e8f2fd;
      border-color: #1e87f0;
      color: #14508f;
    }

    &.danger {
      color: #b03030;
      border-color: #e2b4b4;

      &:hover:not(:disabled) {
        background: #fdeaea;
        border-color: #b03030;
        color: #b03030;
      }
    }

    &.confirm {
      background: #1e87f0;
      border-color: #1e87f0;
      color: #fff;

      &:hover:not(:disabled) {
        background: #1a78d6;
        color: #fff;
      }
    }

    &:disabled {
      opacity: 0.6;
      cursor: default;
    }
  }

  .inline-form {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    gap: 0.75rem;
    margin-top: 0.75rem;
    padding: 0.75rem;
    background: #f4f7fb;
    border: 1px solid #dbe6f3;
    border-radius: 6px;

    label {
      display: flex;
      flex-direction: column;
      font-size: 0.75rem;
      color: #666;
      gap: 0.2rem;

      &.grow {
        flex: 1 1 220px;
      }

      input,
      select {
        padding: 0.35rem 0.5rem;
        border: 1px solid #ccc;
        border-radius: 4px;
        font-size: 0.85rem;
        color: #333;
        background: #fff;
        width: auto;
      }
    }
  }

  .error-text {
    color: #c0392b;
    font-size: 0.85rem;
    margin-top: 0.5rem;
  }

  .installment-panel {
    margin-top: 14px;
    padding-top: 10px;
    border-top: 1px dashed #e9ecef;

    h4 {
      font-size: 0.92rem;
      margin-bottom: 8px;
    }
  }

  .voucher-claim {
    margin-top: 10px;
  }

  .link-button.danger {
    color: #b03030;
  }

  .link-button {
    background: none;
    border: none;
    color: #1e87f0;
    cursor: pointer;
    padding: 0;
    font-size: 0.9rem;

    &:disabled {
      color: #999;
      cursor: default;
    }
  }

  .pagination {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-top: 0.5rem;
  }
}
</style>
